import { useEffect, useMemo, type ReactElement } from 'react';
import { Box, Group, Loader, Stack } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { Coins, ReceiptText, type LucideIcon } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import type { StatItem } from '@/design/StatBand';
import type { BreadcrumbItem } from '@/design/DrillBreadcrumb';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { formatCount, formatUsd } from '@/shared/analytics/MetricFormatting';
import { LicenseCostPageViewModel } from '@/features/licenses/LicenseCostPageViewModel';
import { rawDescription } from '@/features/licenses/CostRecordDimensions';
import type { LicenseCostRecord } from '@/features/licenses/LicensesApiService';
import SpendCostRecordsGrid from './SpendCostRecordsGrid';
import SpendCostBreakdownPanel from './SpendCostBreakdownPanel';
import { SEATS_BUCKET_LABEL } from './SpendCostRows';

interface SpendCostDrillPageProps {
    /** `provider`: all of one connection's records · `model`: one model across providers · `seats`: one connection's flat seat/subscription records. */
    scope: 'provider' | 'model' | 'seats';
}

const COST_BASE_CRUMB: BreadcrumbItem = { label: 'Cost by Provider', to: '/cost' };

/** Cost drill-in: the individual cost records for a provider, a model (across providers), or a provider's seats — a MegaGrid beside a scoped summary + breakdown. */
export default function SpendCostDrillPage({ scope }: SpendCostDrillPageProps): ReactElement {
    const vm = useViewModel(LicenseCostPageViewModel);
    const surface = useSurface('token-usage');
    const { snapshot } = useGlobalFilters();
    const params = useParams<{ connectionId?: string; modelId?: string }>();
    const filterKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

    useEffect(() => {
        void vm.reload();
    }, [vm, filterKey]);

    if (surface === undefined) return <TopicNotFound />;

    const resolved = resolve(scope, params, vm);
    const total = resolved.records.reduce((sum, record) => sum + record.amount, 0);
    const count = resolved.records.length;
    const stats: StatItem[] = [
        { label: 'Total spend', value: formatUsd(total) },
        { label: 'Cost records', value: formatCount(count) },
        { label: 'Avg / record', value: count > 0 ? formatUsd(total / count) : '—' },
    ];

    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <SurfaceBreadcrumbRow items={resolved.breadcrumbs} />

                <PageHeaderComponent icon={resolved.icon} eyebrow={resolved.eyebrow} title={resolved.title} subtitle={resolved.subtitle} />

                {vm.loading && resolved.records.length === 0 ?
                    <Group justify="center" py={64}>
                        <Loader size="sm" />
                    </Group>
                :   <SideRailPageLayout
                        rail={
                            <Stack gap="md">
                                <TopicSummaryPanel stats={stats} />
                                <SpendCostBreakdownPanel title={resolved.breakdownTitle} records={resolved.records} keyOf={resolved.keyOf} currency={vm.currency} variant={resolved.breakdownViz} />
                            </Stack>
                        }
                    >
                        <SpendCostRecordsGrid records={resolved.records} currency={vm.currency} persistenceKey={resolved.persistenceKey} />
                    </SideRailPageLayout>
                }
            </Box>
        </SurfaceAssistScope>
    );
}

interface BreakdownCandidate {
    title: string;
    keyOf: (record: LicenseCostRecord) => string;
    viz: 'donut' | 'bars';
}

interface Resolved {
    title: string;
    eyebrow: string;
    subtitle: string;
    icon: LucideIcon;
    records: LicenseCostRecord[];
    persistenceKey: string;
    breadcrumbs: BreadcrumbItem[];
    breakdownTitle: string;
    keyOf: (record: LicenseCostRecord) => string;
    breakdownViz: 'donut' | 'bars';
}

function monthOf(record: LicenseCostRecord): string {
    return new Date(record.periodStart).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Picks the first breakdown that splits the records into ≥2 slices, so a single-provider model (or single-plan seats) doesn't render a one-slice donut. */
function chooseBreakdown(records: LicenseCostRecord[], candidates: BreakdownCandidate[]): BreakdownCandidate {
    for (const candidate of candidates) {
        const distinct = new Set(records.map(candidate.keyOf));
        if (distinct.size >= 2) return candidate;
    }
    return candidates[candidates.length - 1] ?? candidates[0]!;
}

function resolve(scope: 'provider' | 'model' | 'seats', params: { connectionId?: string; modelId?: string }, vm: LicenseCostPageViewModel): Resolved {
    const labelByConnection = new Map(vm.providerCosts.map((group) => [group.connectionId, group.displayName.length > 0 ? group.displayName : group.typeLabel]));
    const byProvider: BreakdownCandidate = { title: 'Spend by provider', keyOf: (r) => labelByConnection.get(r.providerConnectionId) ?? r.providerConnectionId, viz: 'bars' };
    const byModel: BreakdownCandidate = { title: 'Spend by model', keyOf: (r) => ((r.modelId ?? '').length > 0 ? (r.modelId ?? '') : SEATS_BUCKET_LABEL), viz: 'donut' };
    const byPlan: BreakdownCandidate = { title: 'Spend by plan', keyOf: (r) => rawDescription(r) ?? '—', viz: 'bars' };
    const bySku: BreakdownCandidate = { title: 'Spend by SKU', keyOf: (r) => r.skuId ?? '—', viz: 'bars' };
    const overTime: BreakdownCandidate = { title: 'Spend over time', keyOf: monthOf, viz: 'bars' };

    if (scope === 'provider' || scope === 'seats') {
        const connectionId = decodeURIComponent(params.connectionId ?? '');
        const group = vm.providerCosts.find((entry) => entry.connectionId === connectionId);
        const providerName = group?.displayName !== undefined && group.displayName.length > 0 ? group.displayName : (group?.typeLabel ?? 'Provider');

        if (scope === 'seats') {
            const records = (group?.records ?? []).filter((record) => (record.modelId ?? '').length === 0);
            const breakdown = chooseBreakdown(records, [byPlan, overTime, bySku]);
            return {
                title: SEATS_BUCKET_LABEL,
                eyebrow: providerName,
                subtitle: 'Flat seat and subscription costs recorded for this provider connection.',
                icon: ReceiptText,
                records,
                persistenceKey: `spend-cost-seats:${connectionId}`,
                breadcrumbs: [COST_BASE_CRUMB, { label: providerName, to: `/cost/provider/${encodeURIComponent(connectionId)}` }, { label: SEATS_BUCKET_LABEL }],
                breakdownTitle: breakdown.title,
                keyOf: breakdown.keyOf,
                breakdownViz: breakdown.viz,
            };
        }

        const records = group?.records ?? [];
        const breakdown = chooseBreakdown(records, [byModel, byPlan, overTime, bySku]);
        return {
            title: providerName,
            eyebrow: 'Provider',
            subtitle: 'Every cost record for this provider connection across the selected range.',
            icon: ReceiptText,
            records,
            persistenceKey: `spend-cost-provider:${connectionId}`,
            breadcrumbs: [COST_BASE_CRUMB, { label: providerName }],
            breakdownTitle: breakdown.title,
            keyOf: breakdown.keyOf,
            breakdownViz: breakdown.viz,
        };
    }

    const modelId = decodeURIComponent(params.modelId ?? '');
    const records = vm.providerCosts.flatMap((group) => group.records).filter((record) => record.modelId === modelId);
    const breakdown = chooseBreakdown(records, [byProvider, overTime, bySku]);
    return {
        title: modelId,
        eyebrow: 'Model',
        subtitle: 'Every cost record for this model across all providers in the selected range.',
        icon: Coins,
        records,
        persistenceKey: `spend-cost-model:${modelId}`,
        breadcrumbs: [COST_BASE_CRUMB, { label: modelId }],
        breakdownTitle: breakdown.title,
        keyOf: breakdown.keyOf,
        breakdownViz: breakdown.viz,
    };
}
